import { FC, useState, useMemo } from "react";
import { MobileForm, FormElement } from "@mobileTypes";
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { MobilePhoneFrame } from "./frames/MobilePhoneFrame";
import { EmbeddedListElementPreview } from "./components/EmbeddedListElementPreview";
import { ListItemElementPreview } from "./components/ListItemElementPreview";

interface MobileFormPreviewProps {
  form: Partial<MobileForm>;
}

// Mock command results for preview
const MOCK_COMMAND_RESULTS: Record<string, any[]> = {
  command1: [
    { _id: '1', description: 'Прием багажа, не пригодного для воздушной перевозки' },
    { _id: '2', description: 'Не обращено внимание пассажира на необходимость изъятия опасных вещей' },
    { _id: '3', description: 'Не внесена информация о весе, количестве мест багажа в систему DCS' },
  ],
};

const renderFormElement = (
  element: FormElement,
  index: number,
  isBottomElement: boolean = false,
  selectedIds: string[] = [],
  onCheckboxChange?: (itemId: string, checked: boolean) => void,
  onContextMenuAction?: (action: any, item: any) => void
) => {
  const { type, title, data, textAlign = "LEFT", editable = true } = element;

  const alignment = textAlign.toLowerCase() as "left" | "center" | "right";

  switch (type) {
    case "FIELD":
      if (!editable) {
        return (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              mb: 2,
              mx: 2,
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 80,
                py: 1,
                px: 1.25,
                backgroundColor: "#F3F3F3",
                borderRadius: "8px",
                border: "1px solid #E2E2E2"
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {title || data?.title || "Label"}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                py: 1,
                px: 1.25,
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                border: "1px solid #E2E2E2"
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "#666" }}>Value</Typography>
            </Box>
          </Box>
        );
      }

      // Editable field - стиль как в нативном приложении
      const fieldLabel = data?.title || title || "Field";
      const isRequired = data?.userRequired || data?.defaultRequired;
      const displayLabel = isRequired ? `${fieldLabel} *` : fieldLabel;
      const placeholderText = data?.placeholderValue || `Введите ${fieldLabel.toLowerCase()}`;

      if (data?.type === "enum") {
        // Enum field - отображается как поле с стрелкой вправо
        return (
          <Box
            key={index}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              px: 2,
              py: 1.75,
              mb: 1.5,
              mx: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              "&:hover": {
                backgroundColor: "#FAFAFA"
              }
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                color: "#333",
                fontWeight: 400
              }}
            >
              {displayLabel}
            </Typography>
            <ChevronRightIcon sx={{ color: "#C7C7CC", fontSize: 20 }} />
          </Box>
        );
      }

      // Regular text field
      return (
        <Box
          key={index}
          sx={{
            backgroundColor: "#fff",
            borderRadius: 2,
            px: 1,
            pt: 1.5,
            pb: 0.5,
            mb: 2,
            mx: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
          }}
        >
          <TextField
            fullWidth
            placeholder={placeholderText}
            multiline={data?.isMultiline}
            rows={data?.isMultiline ? 4 : 1}
            variant="standard"
            sx={{
              "& .MuiInput-root": {
                fontSize: "11px",
                color: "#333",
                "&:before": {
                  borderBottom: "none"
                },
                "&:after": {
                  borderBottom: "none"
                },
                "&:hover:not(.Mui-disabled):before": {
                  borderBottom: "none"
                }
              },
              "& .MuiInputBase-input": {
                padding: 0,
                fontSize: "11px",
                "&::placeholder": {
                  color: "#ACACAC",
                  opacity: 1
                }
              }
            }}
          />
        </Box>
      );

    case "TITLE":
      return (
        <Box key={index} sx={{ px: 2, py: 1, mt: 2, mb: 0.5 }}>
          <Typography
            sx={{
              textAlign: alignment,
              fontWeight: 700,
              fontSize: "17px",
              color: "#000"
            }}
          >
            {title || "Title"}
          </Typography>
        </Box>
      );

    case "DIVIDER":
      return (
        <Box key={index} sx={{ width: "100%", px: 2, my: 2 }}>
          <Divider sx={{ borderColor: "#E5E5E5" }} />
        </Box>
      );

    case "BUTTON":
      return (
        <Box
          key={index}
          sx={{ width: "100%", px: isBottomElement ? 0 : 2, mb: isBottomElement ? 0 : 1 }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              textAlign: alignment,
              fontSize: "11px",
              fontWeight: 400,
              textTransform: "none",
              borderRadius: "24px",
              backgroundColor: "#007AFF",
              height: "32px",
              color: "#fff",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#0051D5",
                boxShadow: "none"
              }
            }}
          >
            {title || element.command?.name || "Button"}
          </Button>
        </Box>
      );

    case "SWIPE":
      return (
        <Box key={index} sx={{ width: "100%", px: 0, my: 2 }}>
          <Box
            sx={{
              position: "relative",
              height: "50px",
              width: "100%",
              background: "linear-gradient(90deg, #06d6a0 0%, #1b9aaa 100%)",
              borderRadius: "25px",
              padding: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden"
            }}
          >
            {/* White circle (swipeable element) */}
            <Box
              sx={{
                position: "absolute",
                left: "10px",
                height: "30px",
                width: "30px",
                borderRadius: "50%",
                backgroundColor: "#fff",
                zIndex: 3
              }}
            />
            {/* Text */}
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{
                color: "#fff",
                zIndex: 2,
                fontSize: "14px"
              }}
            >
              {title || "Swipe to confirm"}
            </Typography>
            {element.actions && element.actions.length > 0 && (
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: "-18px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "text.secondary",
                  fontSize: "10px"
                }}
              >
                {element.actions.length} action(s)
              </Typography>
            )}
          </Box>
        </Box>
      );

    case "EMBEDDED_LIST":
      return (
        <Box key={index} sx={{ width: "100%", px: 2, mb: 2 }}>
          {element.embeddedListItemId || element.embeddedListConfig ? (
            <EmbeddedListElementPreview
              element={element}
              commandResults={MOCK_COMMAND_RESULTS}
              selectedIds={selectedIds}
              onCheckboxChange={onCheckboxChange || (() => {})}
              onContextMenuAction={onContextMenuAction || (() => {})}
            />
          ) : (
            <Box sx={{ p: 2, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Configure embedded list
              </Typography>
            </Box>
          )}
        </Box>
      );

    case "LIST_ITEM":
      return (
        <Box key={index} sx={{ width: "100%", px: 2, mb: 2 }}>
          {element.listItemConfigId ? (
            <ListItemElementPreview
              element={element}
            />
          ) : (
            <Box sx={{ p: 2, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Configure list item
              </Typography>
            </Box>
          )}
        </Box>
      );

    default:
      return null;
  }
};

export const MobileFormPreview: FC<MobileFormPreviewProps> = ({ form }) => {
  const elements = form.elements || [];
  const bottomElements = form.bottomElements || [];

  // Управление выбранными элементами для EMBEDDED_LIST
  const [selectedIdsMap, setSelectedIdsMap] = useState<Record<string, string[]>>({});

  // Получаем переменные для хранения selectedIds
  const variables = form.variables || [];

  const handleCheckboxChange = (elementIndex: number, itemId: string, checked: boolean) => {
    const element = elements[elementIndex];
    if (element?.type !== 'EMBEDDED_LIST') return;

    const variableName = element.selectedIdsVariable;
    if (!variableName) return;

    setSelectedIdsMap(prev => {
      const currentIds = prev[variableName] || [];
      const newIds = checked
        ? [...currentIds, itemId]
        : currentIds.filter(id => id !== itemId);

      return {
        ...prev,
        [variableName]: newIds,
      };
    });
  };

  const handleContextMenuAction = (action: any, item: any) => {
    // В превью просто показываем, что действие было вызвано
    console.log('Context menu action:', action, item);
  };

  const getSelectedIdsForElement = (element: FormElement): string[] => {
    if (element.type !== 'EMBEDDED_LIST' || !element.selectedIdsVariable) {
      return [];
    }
    return selectedIdsMap[element.selectedIdsVariable] || [];
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
      <MobilePhoneFrame title="📱 Form Preview">
        <Box
          sx={{
            height: "100%",
            overflow: "auto",
            scrollbarWidth: "thin",
            backgroundColor: "#f2f2f2",
            display: "flex",
            flexDirection: "column",
            pb: bottomElements.length > 0 ? "90px" : "0"
          }}
        >
          {/* Main form elements */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              pt: 2
            }}
          >
            {
              elements.map((element, index) =>
                renderFormElement(
                  element,
                  index,
                  false,
                  getSelectedIdsForElement(element),
                  (itemId: string, checked: boolean) => handleCheckboxChange(index, itemId, checked),
                  handleContextMenuAction
                )
              )
            }
          </Box>

          {/* Bottom elements (buttons) - fixed at bottom */}
          {bottomElements.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                px: 2,
                pt: 2.5,
                backgroundColor: "white",
                borderTop: "0.5px solid #E5E5E5",
                boxShadow: "0px 0px 10px rgba(0,0,0,0.2)"
              }}
            >
              {bottomElements.map((element, index) => renderFormElement(element, index, true))}
            </Box>
          )}
        </Box>
      </MobilePhoneFrame>
    </Box>
  );
};
